import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, CardFooter, Stepper } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useProcess, useProcessActions } from '@/api/capacities';
import type { ImportItemMetadata, Item, ProcessType, WarehouseProcess } from '@/api/capacities';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ItemSelectorDialog } from './item-selector-dialog';
import {
  DetailsStep,
  ReviewStep,
  TypeStep,
  buildCreatePayload,
  buildUpdatePayload,
  canSubmit,
  processToWizardState,
  validateWizard,
  type WizardState,
} from './wizard-steps';

function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })
    ?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

const WIZARD_STEPS = [
  { label: 'Type', description: 'Process kind' },
  { label: 'Details', description: 'Reason & endpoints' },
  { label: 'Review', description: 'Confirm & submit' },
];

const initialState: WizardState = {
  type: undefined,
  reason: '',
  // Default to today, matching the legacy create form's pre-filled process date.
  processDate: new Date().toISOString().split('T')[0],
  expectedDate: '',
  description: '',
  notes: '',
  source: '',
  destination: '',
  sourceName: '',
  destinationName: '',
  items: [],
  importItems: [],
};

/** Only draft / pending processes can be edited (legacy `canEdit`). */
const EDITABLE_STATUSES = new Set(['draft', 'pending_approval']);

/**
 * `/capacities/processes/new` (create) and `/capacities/processes/:id/edit`
 * (edit) — the process wizard (Type → Details → Review). In edit mode the
 * existing process prefills the state, the type is locked, and submit PUTs to
 * `/processes/:id` instead of POSTing. Only draft / pending processes are
 * editable, matching legacy.
 *
 * Data-safety: the final submit hits the live warehouse service and is performed
 * by the user. The Review step carries a "live action" note.
 */
export default function CapacityProcessNewPage() {
  const navigate = useNavigate();
  const params = useParams();
  const editId = params.id;
  const isEdit = Boolean(editId);

  const { data: existing, isLoading: loadingExisting, isError: existingError, refetch } = useProcess(editId ?? '');

  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>(initialState);
  const [prefilled, setPrefilled] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const { create, update } = useProcessActions();
  const mutation = isEdit ? update : create;
  const errors = showErrors ? validateWizard(state) : {};

  // Prefill wizard state once the existing process loads (edit mode).
  useEffect(() => {
    if (isEdit && existing && !prefilled) {
      setState(processToWizardState(existing));
      setPrefilled(true);
    }
  }, [isEdit, existing, prefilled]);

  const setField = <K extends keyof WizardState>(key: K, value: WizardState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const setType = (type: ProcessType) => setState((s) => ({ ...s, type }));

  const addItem = (item: Item) =>
    setState((s) =>
      s.items.some((i) => i.id === item.id) ? s : { ...s, items: [...s.items, item] },
    );

  const removeItem = (id: string) =>
    setState((s) => ({ ...s, items: s.items.filter((i) => i.id !== id) }));

  const addImportItem = (item: ImportItemMetadata) =>
    setState((s) => ({ ...s, importItems: [...s.importItems, item] }));

  const removeImportItem = (index: number) =>
    setState((s) => ({ ...s, importItems: s.importItems.filter((_, i) => i !== index) }));

  const canContinue = step === 0 ? Boolean(state.type) : true;
  const isLast = step === WIZARD_STEPS.length - 1;

  // Advance a step; on the Details step, block + surface errors when invalid.
  const goNext = () => {
    if (step === 1 && Object.keys(validateWizard(state)).length > 0) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setStep(step + 1);
  };

  const submit = () => {
    if (isEdit && editId) {
      update.mutate(
        { id: editId, body: buildUpdatePayload(state) },
        { onSuccess: () => navigate(`/capacities/processes/${editId}`) },
      );
    } else {
      create.mutate(buildCreatePayload(state), {
        onSuccess: (process: WarehouseProcess) => navigate(`/capacities/processes/${process.id}`),
      });
    }
  };

  // Edit-mode gates: loading, load error, and non-editable status.
  if (isEdit && loadingExisting) {
    return (
      <Container size="full">
        <TabLoading rows={4} />
      </Container>
    );
  }
  if (isEdit && existingError) {
    return (
      <Container size="full">
        <TabError title="Failed to load process" onRetry={() => refetch()} />
      </Container>
    );
  }
  if (isEdit && existing && !EDITABLE_STATUSES.has(existing.status)) {
    return (
      <Container size="full">
        <PageHeader title="Edit process" style={{ marginBottom: 'var(--vn-space-4)' }} />
        <Alert tone="warning" title={`Cannot edit a ${existing.status.replace('_', ' ')} process`}>
          Only draft or pending-approval processes can be edited.{' '}
          <Button variant="ghost" onClick={() => navigate(`/capacities/processes/${editId}`)}>
            Back to process
          </Button>
        </Alert>
      </Container>
    );
  }

  const title = isEdit ? 'Edit process' : 'New process';
  const description = isEdit
    ? 'Update this warehouse process. The type is fixed after creation.'
    : 'Create a warehouse import, export, transfer or inventory process.';

  return (
    <Container size="full">
      <PageHeader title={title} description={description} style={{ marginBottom: 'var(--vn-space-4)' }} />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Stepper
          steps={WIZARD_STEPS}
          current={step}
          reached={step}
          onStepClick={(i) => i <= step && setStep(i)}
          aria-label="Process wizard progress"
        />

        <Card flat>
          <CardBody>
            {step === 0 ? (
              <TypeStep value={state.type} onChange={setType} disabled={isEdit} />
            ) : step === 1 ? (
              <DetailsStep
                state={state}
                onField={setField}
                onOpenItems={() => setItemsOpen(true)}
                onRemoveItem={removeItem}
                onAddImportItem={addImportItem}
                onRemoveImportItem={removeImportItem}
                errors={errors}
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <ReviewStep state={state} />
                {mutation.isError ? (
                  <Alert tone="danger" title={isEdit ? 'Could not update process' : 'Could not create process'}>
                    {errorMessage(mutation.error)}
                  </Alert>
                ) : null}
                <Alert tone="warning" title="Live action">
                  {isEdit
                    ? 'Saving updates a real warehouse process via the live service. Review the details above before continuing.'
                    : 'Submitting creates a real warehouse process via the live service. Review the details above before continuing.'}
                </Alert>
                {!canSubmit(state) ? (
                  <Alert tone="info" title="Complete the required fields">
                    Fill in the reason, required endpoints and at least one item on the Details step
                    before submitting.
                  </Alert>
                ) : null}
              </div>
            )}
          </CardBody>
          <CardFooter style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
            <Button
              variant="ghost"
              startIcon={<ArrowLeft size={16} />}
              onClick={() =>
                step === 0
                  ? navigate(isEdit ? `/capacities/processes/${editId}` : '/capacities/processes')
                  : setStep(step - 1)
              }
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            {isLast ? (
              <Button
                variant="primary"
                loading={mutation.isPending}
                disabled={!canSubmit(state) || mutation.isPending}
                onClick={submit}
              >
                {isEdit ? 'Save changes' : 'Create process'}
              </Button>
            ) : (
              <Button
                variant="primary"
                endIcon={<ArrowRight size={16} />}
                disabled={!canContinue}
                onClick={goNext}
              >
                Continue
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>

      <ItemSelectorDialog
        open={itemsOpen}
        onOpenChange={setItemsOpen}
        onAdd={addItem}
        selectedIds={state.items.map((i) => i.id)}
      />
    </Container>
  );
}
